import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';

const { FiServer, FiGlobe, FiLayers, FiShield, FiTrendingUp, FiMapPin, FiArrowRight, FiBookOpen } = FiIcons;

const Knowledge = () => {
  const categories = [
    {
      icon: FiServer,
      title: 'Web hosting guides',
      description: 'Comprehensive guides to help you choose and manage your hosting',
      color: 'bg-blue-100 text-blue-600',
      articles: 6
    },
    {
      icon: FiGlobe,
      title: 'Domain advice',
      description: 'Everything you need to know about domain names and management',
      color: 'bg-green-100 text-green-600',
      articles: 4
    },
    {
      icon: FiLayers,
      title: 'Website design tips',
      description: 'Best practices for creating effective, user-friendly websites',
      color: 'bg-purple-100 text-purple-600',
      articles: 8
    },
    {
      icon: FiShield,
      title: 'Security best practices',
      description: 'Protect your website and business from online threats',
      color: 'bg-red-100 text-red-600',
      articles: 5
    },
    {
      icon: FiMapPin,
      title: 'South African insights',
      description: 'Local hosting advantages and market-specific guidance',
      color: 'bg-yellow-100 text-yellow-600',
      articles: 3
    },
    {
      icon: FiTrendingUp,
      title: 'Performance optimisation',
      description: 'Speed up your website and improve search engine rankings',
      color: 'bg-teal-100 text-teal-600',
      articles: 7
    }
  ];

  const featuredArticles = [
    {
      title: 'Choosing the right hosting plan for your South African business',
      excerpt: 'Learn how to evaluate your hosting needs and select the perfect plan that balances performance, features, and cost for your business requirements.',
      category: 'Web hosting guides',
      readTime: '8 min read',
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      featured: true
    },
    {
      title: 'Domain name selection: Building your brand online',
      excerpt: 'Discover the key principles of choosing a domain name that strengthens your brand, improves SEO, and resonates with your target audience.',
      category: 'Domain advice',
      readTime: '6 min read',
      image: 'https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      title: 'Mobile-responsive design: Why it matters in 2024',
      excerpt: 'Understand the importance of mobile-responsive design and how it impacts user experience, SEO rankings, and business success.',
      category: 'Website design tips',
      readTime: '10 min read',
      image: 'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    }
  ];

  const popularArticles = [
    {
      title: 'Website security essentials: Protecting your business online',
      category: 'Security best practices',
      readTime: '12 min read'
    },
    {
      title: 'Local hosting advantages for South African businesses',
      category: 'South African insights',
      readTime: '7 min read'
    },
    {
      title: 'SEO optimisation: Getting found by your customers',
      category: 'Performance optimisation',
      readTime: '15 min read'
    },
    {
      title: 'Email hosting setup: Professional communication guide',
      category: 'Web hosting guides',
      readTime: '9 min read'
    },
    {
      title: 'Website speed optimisation techniques',
      category: 'Performance optimisation',
      readTime: '11 min read'
    }
  ];

  const quickTips = [
    {
      title: 'Regular backups',
      description: 'Always maintain current backups of your website and data'
    },
    {
      title: 'SSL certificates',
      description: 'Ensure your website has a valid SSL certificate for security and SEO'
    },
    {
      title: 'Mobile testing',
      description: 'Test your website on various mobile devices regularly'
    },
    {
      title: 'Performance monitoring',
      description: 'Monitor your website speed and uptime consistently'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-20"
    >
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Knowledge base
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Expert guides, tips, and insights to help you make the most of your web hosting, design, and domain services. Learn from our decade of experience serving South African businesses.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <div className="bg-primary-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-primary-500 mb-1">30+</div>
                <div className="text-sm text-gray-600">Expert articles</div>
              </div>
              <div className="bg-primary-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-primary-500 mb-1">6</div>
                <div className="text-sm text-gray-600">Topic categories</div>
              </div>
              <div className="bg-primary-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-primary-500 mb-1">10+</div>
                <div className="text-sm text-gray-600">Years of insights</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Browse by category
            </h2>
            <p className="text-xl text-gray-600">
              Find the information you need organised by topic
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300 cursor-pointer"
              >
                <div className={`w-12 h-12 rounded-xl ${category.color} flex items-center justify-center mb-4`}>
                  <SafeIcon icon={category.icon} className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {category.title}
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {category.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    {category.articles} articles
                  </span>
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4 text-primary-500" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Featured articles
            </h2>
            <p className="text-xl text-gray-600">
              Our most comprehensive and popular guides
            </p>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Main Featured Article */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:row-span-2"
            >
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden h-full">
                <div className="relative">
                  <img
                    src={featuredArticles[0].image}
                    alt={featuredArticles[0].title}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      Featured
                    </span>
                  </div>
                </div>
                <div className="p-8">
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                    <span>{featuredArticles[0].category}</span>
                    <span>•</span>
                    <span>{featuredArticles[0].readTime}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {featuredArticles[0].title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {featuredArticles[0].excerpt}
                  </p>
                  <button className="flex items-center space-x-2 text-primary-500 font-semibold hover:text-primary-600 transition-colors duration-200">
                    <span>Read article</span>
                    <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>

            {/* Secondary Featured Articles */}
            <div className="space-y-8">
              {featuredArticles.slice(1).map((article, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-48 md:h-full object-cover"
                    />
                    <div className="p-6">
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                        <span>{article.category}</span>
                        <span>•</span>
                        <span>{article.readTime}</span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-3">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed mb-4">
                        {article.excerpt}
                      </p>
                      <button className="flex items-center space-x-2 text-primary-500 font-medium hover:text-primary-600 transition-colors duration-200">
                        <span>Read more</span>
                        <SafeIcon icon={FiArrowRight} className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Popular Articles & Quick Tips */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Popular Articles */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-8">
                Popular articles
              </h2>
              <div className="space-y-6">
                {popularArticles.map((article, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
                  >
                    <div className="bg-primary-100 p-2 rounded-lg">
                      <SafeIcon icon={FiBookOpen} className="w-5 h-5 text-primary-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {article.title}
                      </h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{article.category}</span>
                        <span>•</span>
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                    <SafeIcon icon={FiArrowRight} className="w-4 h-4 text-gray-400" />
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Quick Tips */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-8">
                Quick tips
              </h2>
              <div className="space-y-6">
                {quickTips.map((tip, index) => (
                  <div key={index} className="bg-primary-50 p-4 rounded-xl">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {tip.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {tip.description}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: '#D14932' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Need personalised guidance?
            </h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              Our expert team is ready to provide tailored advice for your specific hosting, design, and domain requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-red-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              >
                Contact our experts
              </Link>
              <Link
                to="/services"
                className="border border-red-300 text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-600 transition-colors duration-200"
              >
                View our services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Knowledge;